import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatPhoneNumber } from '@/utils/phoneUtils';
import { createStandardizedFormSubmission } from '@/utils/formSubmissionUtils';
import { getStoredInterestData } from '@/utils/interestDataUtils';

interface OfferFormProps {
  formData: { [key: string]: string };
  onUpdateData: (data: { [key: string]: string }) => void;
  onSubmit?: (formData: { [key: string]: string }) => void;
  title?: string;
}

const OfferForm: React.FC<OfferFormProps> = ({
  formData,
  onUpdateData,
  onSubmit,
  title,
}) => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Local state still used only for initial AI pre-fill metadata
  const [localFormData, setLocalFormData] = useState(formData);
  const [hasUserEdited, setHasUserEdited] = useState(false);

  // Uncontrolled input refs – actual text lives in the DOM, not React state
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);

  // Sync local state when formData changes (AI pre-fill),
  // but stop syncing once the user starts typing to avoid cursor glitches
  useEffect(() => {
    if (!hasUserEdited) {
      setLocalFormData(formData);
    }
  }, [formData, hasUserEdited]);
  
  const handleInputChange = () => {
    if (!hasUserEdited) {
      setHasUserEdited(true);
    }
  };
  

  const handleFormSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const name = nameInputRef.current?.value || '';
      const phone = phoneInputRef.current?.value || '';
      const interest = formData.interest || '';

      // Get stored interest data
      const storedInterest = await getStoredInterestData();
 
      // Create offer-specific interest data with message
      const offerMessage = language === 'fr'
        ? `Demande d'offre - ${name} a demandé une offre. Contact: ${phone}`
        : `Offer Request - ${name} has requested an offer. Contact: ${phone}`;
 
      const offerInterestData = {
        ...storedInterest,
        message: offerMessage,
        department: storedInterest?.department || 'New'
      };
 
      const submissionData = createStandardizedFormSubmission(
        {
          name,
          phone,
          interest: interest || ''
        },
        'offer',
        formatPhoneNumber,
        offerInterestData
      );
 
      if (onSubmit) {
        await onSubmit(submissionData);
      }

      // Optionally push final values back into parent formData once on submit
      onUpdateData({ ...formData, name, phone, interest: interest || '' });
      
      // Show success feedback
      toast.success(language === 'fr' ? 'Demande envoyée avec succès!' : 'Request sent successfully!');
    } catch (error) {
      console.error('Offer form submission error:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const name = nameInputRef.current?.value || '';
    const phone = phoneInputRef.current?.value || '';
    return name.trim().length > 0 && phone.trim().length > 0;
  };

  const phoneLabel = t('form.mobile');

  return (
    <div className="p-2">
      <div className="mb-3">
        {/* Hidden Interest field */}
        <input type="hidden" name="interest" value={localFormData.interest || ''} />
        
        {/* Hidden topcarid field */}
        <input 
          id="topcarid" 
          name="topcarid" 
          type="hidden" 
          value={(() => {
            try {
              const topCarIdElement = document.getElementById('topcarid') as HTMLInputElement;
              return topCarIdElement?.value ? `Vehicle ID: ${topCarIdElement.value}` : '';
            } catch {
              return '';
            }
          })()}
        />
        
        {/* All fields on one row */}
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1">
             <Label htmlFor="name" className="text-xs text-gray-700">{t('form.name')}</Label>
             <Input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                defaultValue={localFormData.name || ''}
                ref={nameInputRef}
                onChange={handleInputChange}
                placeholder={t('form.name')}
                className="h-7 text-sm"
              />
           </div>
           <div className="w-36 space-y-1">
             <Label htmlFor="phone" className="text-xs text-gray-700">{phoneLabel}</Label>
            <Input
               id="phone"
               name="phone"
               type="tel"
               autoComplete="tel"
               defaultValue={localFormData.phone || ''}
               ref={phoneInputRef}
               onChange={handleInputChange}
               placeholder="555-555-1212"
               className="h-7 text-sm"
             />
          </div>
          <Button
            type="button"
            size="icon"
            onClick={handleFormSubmit}
            disabled={!isFormValid() || isSubmitting}
            className="h-7 w-7 min-w-7 bg-red-600 hover:bg-red-700 flex-shrink-0 mb-[1px]"
            aria-label={isSubmitting ? t('form.submitting') : t('form.submit')}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OfferForm;
